/* eslint-disable no-unused-vars */
import { createStore } from "vuex";
import BigNumber from "bignumber.js";
// import createPersistedState from "vuex-persistedstate";
import Web3Modal from "@/factories/web3/Web3Modal";
import sleep from "@/factories/Sleep";
import ERC20 from "@/factories/web3/ERC20";
import BSC_TESTNET from "@/factories/web3/BSC_TESTNET";
import CONFIG_VARIABLES from "@/constants/CONFIG_VARIABLES";
import moment from "moment-mini";

const store = createStore({
  state() {
    return {
      unlockDateConst: 10,
      vestingsList: [],

      initLoading: false,
      web3: {
        instance: null,
        isConnected: false,
        chainId: null,
        provider: null,
        address: "",
        mainCurrencyBalance: "",
      },
      transactionError: "",
      selectedTokenInfo: {
        address: "",
        name: "",
        symbol: "",
        decimals: "18",
        userBalance: "",
      },
      tokens: [],
      eth: {
        networks: [
          {
            name: "Binance Smart Chain",
            short_name: "bsc",
            chain: "BSC",
            network: "mainnet",
            chain_id: 56,
            network_id: 56,
            explorer_url: "https://bscscan.com/tx/",
            rpc_url: "https://bsc-dataseed.binance.org/",
            blocks_per_day: 28800,
            gas: 1700000,
            native_currency: {
              symbol: "BNB",
              name: "BNB",
              decimals: 18,
              contractAddress: "",
              balance: "",
            },
            contracts: {},
            tokens: [
              {
                symbol: "USDT",
                address: CONFIG_VARIABLES.USDT_CONTRACT_ADDRESS,
              },
              {
                symbol: "NYS",
                address: "0x279960F9CB21965b33Ad3F3Ced07349363cB2030",
              },
            ],
          },
          {
            name: "Binance Smart Chain Testnet",
            short_name: "bsc",
            chain: "BSC",
            network: "mainnet",
            chain_id: 97,
            network_id: 97,
            explorer_url: "https://testnet.bscscan.com/tx/",
            rpc_url: "https://data-seed-prebsc-1-s1.binance.org:8545/",
            blocks_per_day: 28800,
            gas: 1700000,
            native_currency: {
              symbol: "ECELL",
              name: "ECELL",
              decimals: 18,
              contractAddress: "",
              balance: "",
            },
            contracts: {},
            tokens: [
              {
                symbol: "USDT",
                address: "0xdb7558fb5a652d674920e65a7232fefaa7030349",
              },
              {
                symbol: "NYS",
                address: "0x5297B179E0D217E8046a93F6D6D60d0c3A2D0B34",
              },
            ],
          },
        ],
      },
      tokenPrice: 0.35,
      transactionHash: "",
      maximumAmount: null,
      minimumAmount: null,
    };
  },
  getters: {
    isConnected(state) {
      return state.web3.isConnected;
    },

    initLoading(state) {
      return state.initLoading;
    },

    activeNetwork(state) {
      return state.eth.networks.find((n) => {
        return n.chain_id === state.web3.chainId;
      });
    },

    tokenPrice(state) {
      return state.tokenPrice;
    },

    walletAddress(state) {
      return state.web3?.address;
    },

    userBalance(state) {
      return state.tokens[0]?.userBalance;
    },

    currentExplorerUrl(_, getters) {
      return getters.activeNetwork?.explorer_url;
    },

    transactionHash(state) {
      return state.transactionHash;
    },

    tokens(state) {
      return state.tokens;
    },

    chosenToken(state) {
      return state.tokens[0];
    },

    transactionError(state) {
      return state.transactionError;
    },

    isCorrectNetwork(state) {
      return state.web3.chainId === CONFIG_VARIABLES.CORRECT_NETWORK_ID
    },

    maximumAmount(state) {
      return state.maximumAmount
    },

    minimumAmount(state) {
      return state.minimumAmount
    },

    vestingSummaryUnlocked(state) {
      if (state.vestingsList.length) {
        const totalAmountVestingLock = state.vestingsList.reduce((sum, item) => {
          return sum + parseFloat(item.amount_unlock);
        }, 0);
        return totalAmountVestingLock
      }
    },

    vestingSummaryLocked(state) {
      if (state.vestingsList.length) {
        const totalAmountVestingLock = state.vestingsList.reduce((sum, item) => {
          return sum + parseFloat(item.amount_vesting_lock);
        }, 0);
        return totalAmountVestingLock
      }
    },

    vestingSummaryBalance(state) {
      if (state.vestingsList.length) {
        const totalAmountVestingBalance = state.vestingsList.reduce((sum, item) => {
          return sum + parseFloat(item.balance);
        }, 0);
        return totalAmountVestingBalance
      }
    },

    nextUnlockDate(state) {
      if (state.web3?.isConnected) {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth();
        const day = today.getDate();
        let nextTenth;
        if (day < state.unlockDateConst) {
          nextTenth = new Date(year, month, state.unlockDateConst);
        } else {
          if (month === 11) {
            nextTenth = new Date(year + 1, 0, 10);
          } else {
            nextTenth = new Date(year, month + 1, 10);
          }
        }

        return moment(nextTenth).format("DD.MM.YYYY");
      } else {
        return "-"
      }
    },

    vestingsList(state) {
      return state.vestingsList;
    }
  },
  actions: {
    async init({ commit, dispatch, getters, state }, reset = false) {
      try {
        if (state.web3?.isConnected && !reset) {
          return;
        }
        const { provider, web3 } = await Web3Modal.connect();
        commit("SET_WEB3_PROVIDER", provider);
        commit("SET_WEB3_INSTANCE", web3);
        const resetConnection = async () => {
          dispatch("disconnect");
          await dispatch("init", true);
          localStorage.cellLoggedIn = true;
          localStorage.WEB3_CONNECT_CACHED_PROVIDER = '"injected"';
        };
        Web3Modal.bindProviderEvents({
          accountsChanged: resetConnection,
          chainChanged: resetConnection,
          disconnect: () => dispatch("disconnect"),
        });
        const chainId = await web3.eth.getChainId()
        commit("SET_WEB3_CHAIN_ID", chainId);
        commit("SET_WEB3_IS_CONNECTED", true);
        const [accountAddr] = await web3.eth.getAccounts();
        commit("SET_WEB3_USER_ADDRESS", accountAddr);
        if (getters.isCorrectNetwork) {
          dispatch("getTokensInfo", getters.activeNetwork.tokens);
        }
      } catch (error) {
        console.log("wallet connect error");
        console.log(error.message);
      } finally {
        localStorage.cellLoggedIn = true;
      }
    },

    async getVestingsInfo({ commit, getters }) {
      try {
        commit("SET_INIT_LOADING", true);
        const userAddr = getters.walletAddress;
        const origin = window.location.origin;
        const cookieToken = document.cookie.match(/csrftoken=[^\s;]+/);
        if (!cookieToken) {
          return;
        }
        console.log("userAddr")
        console.log(userAddr)
        const token = cookieToken[0].split("=")[1]
        const response = await fetch(origin + `/api/uservesting/${userAddr}`, {
          method: "GET",
          headers: {
            "X-CSRFToken": token,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Something went wrong: ${response.status}`);
        }

        const data = await response.json();
        console.log("vestings list data")
        console.log(data)
        commit("SET_VESTINGS_LIST", data)
      } catch (error) {
        console.error(error.message);
        return null;
      } finally {
        commit("SET_INIT_LOADING", false);
      }
    },

    async getCellInfo({ dispatch, getters, state }, { selectedToken }) {
      try {
        const activeNetwork = getters.activeNetwork;

        if (!activeNetwork) {
          await sleep(500);
          return await dispatch("getCellInfo", selectedToken);
        }
      } catch (error) {
        console.error(error.message);
        return null;
      }
    },

    disconnect({ commit, state }) {
      commit("SET_WEB3_PROVIDER", null);
      commit("SET_WEB3_INSTANCE", null);
      commit("SET_WEB3_IS_CONNECTED", false);
      commit("SET_WEB3_CHAIN_ID", null);
      commit("SET_WEB3_USER_ADDRESS", "");

      // Clear cached provider to be able to switch between providers when disconnecting wallet
      Web3Modal.clearCachedProvider();
      localStorage.removeItem("cellLoggedIn");
      commit("SET_VESTINGS_LIST", [])
    },

    async getCurrentTokenRate(
      { commit, state },
      contractAddress = "0xcBB8c4a012Fb0884826988d17352F30C9d92D251"
    ) {
      try {
        const web3 = state.web3.instance;
        const contract = await BSC_TESTNET(web3, contractAddress);
        const currentPriceWei = await contract.methods.priceNYS().call();
        const currentPriceNYS = web3.utils.fromWei(currentPriceWei, "ether");
        console.log("currentPriceNYS");
        console.log(currentPriceNYS);
        commit("SET_TOKEN_PRICE", currentPriceNYS);
      } catch (error) {
        console.log("get current token rate error");
        console.log(error.message);
      }
    },

    async getCurrentMinMaxAbount(
      { commit, state },
      contractAddress = "0xcBB8c4a012Fb0884826988d17352F30C9d92D251"
    ) {
      try {
        const web3 = state.web3.instance;
        const contract = await BSC_TESTNET(web3, contractAddress);
        const minimumAmount = await contract.methods.minimalAmount().call();
        const maximumAmount = await contract.methods.maximalAmount().call();
        commit("SET_MIN_AMOUNT", new BigNumber(minimumAmount).div(new BigNumber(10).pow(18)));
        commit("SET_MAX_AMOUNT", new BigNumber(maximumAmount).div(new BigNumber(10).pow(18)));
      } catch (error) {
        console.log("get current min max amount error");
        console.log(error.message);
      }
    },

    async checkPromo({ commit }, code) {
      try {
        commit("SET_INIT_LOADING", true);
        const origin = window.location.origin;
        const cookieToken = document.cookie.match(/csrftoken=[^\s;]+/);
        if (!cookieToken) {
          return;
        }
        const token = cookieToken[0].split("=")[1]
        const response = await fetch(origin + "/api/check-promo/ ", {
          method: "POST",
          headers: {
            "X-CSRFToken": token,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ code }),
        });
        console.log("checkPromo response");
        console.log(response);
        console.log(response.ok);
        console.log("typeof response.ok");
        console.log(typeof response.ok);
        return response.ok
      } catch (error) {
        console.log("promo error");
        console.log(error);
        return false
      } finally {
        commit("SET_INIT_LOADING", false);
      }
    },

    async changeNetworkToCorrect({ state, commit, dispatch }) {
      commit("SET_INIT_LOADING", true);
      try {
        const web3 = state.web3;
        await web3.provider.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: CONFIG_VARIABLES.CORRECT_NETWORK_ID_HEX }],
        });
      } catch (error) {
        console.log("change network to correct error")
        console.log(error)
      } finally {
        commit("SET_INIT_LOADING", false);
      }
    },

    async buyToken({ state, commit, dispatch }, { amountUSDT, promocode, name, email, amountNYSForHistory, amountUSDTForHistory }) {
      try {
        commit("SET_INIT_LOADING", true);
        const web3 = state.web3.instance;
        const userAddr = state.web3.address;
        const contractAddress = "0xcBB8c4a012Fb0884826988d17352F30C9d92D251";
        const contract = await BSC_TESTNET(web3, contractAddress);
        const gasPrice = await dispatch("getCurrentGasPrice");

        const selectedTokenCont = await ERC20(
          web3,
          CONFIG_VARIABLES.USDT_CONTRACT_ADDRESS
        );
        const approveRes = await selectedTokenCont.methods
          .approve(contractAddress, amountUSDT)
          .send(
            {
              from: userAddr,
              to: contractAddress,
              gasPrice,
            },
            function (err, transactionHash) {
              if (!err) console.log(transactionHash + " success");
            }
          )
          .on("receipt", function (receipt) {
            localStorage.transactionHashApproval = receipt.transactionHash;
          });
        console.log("APPROVE RES");
        console.log(approveRes);

        const response = await contract.methods
          .buyNYS(amountUSDT, promocode)
          .send(
            {
              from: userAddr,
              to: contractAddress,
              gasPrice,
            },
            function (err, transactionHash) {
              if (!err) console.log(transactionHash + " success");
            }
          )
          .on("receipt", function (receipt) {
            localStorage.transactionHashApproval = receipt.transactionHash;
            commit("SET_TRANSACTION_HASH", receipt.transactionHash);
            const successfullTransactionObj = {
              transaction_hash: receipt.transactionHash,
              sender_address: userAddr,
              amount_nys: amountNYSForHistory,
              amount_usdt: amountUSDTForHistory,
              promo_code: promocode,
              price_nys: state.tokenPrice,
              full_name: name,
              email: email,
              timestamp: Math.floor(Date.now() / 1000),
              block_number: receipt.blockNumber,
              status: "Completed"
            }
            dispatch("sendNotification", successfullTransactionObj);
          });
        console.log("BUY RES");
        console.log(response);
      } catch (error) {
        console.log("buy token error");
        console.log(error);
        commit("SET_TRANSACTION_ERROR", error)
      } finally {
        commit("SET_INIT_LOADING", false);
      }
    },

    async sendNotification({ state, commit, dispatch }, obj) {
      try {
        const cookieToken = document.cookie.match(/csrftoken=[^\s;]+/);
        if (!cookieToken) {
          return;
        }
        const token = cookieToken[0].split("=")[1]
        const response = await fetch(origin + "/api/transactions/", {
          method: "POST",
          headers: {
            "X-CSRFToken": token,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(obj),
        });
        console.log("response")
        console.log(response)
      } catch (error) {
        console.log("sendNotification error");
        console.log(error);
        commit("SET_TRANSACTION_ERROR", error)
      }
    },

    async getCurrentGasPrice({ state }) {
      const web3 = state.web3.instance;
      const gasPrice = web3.eth.getGasPrice();
      return gasPrice;
    },
    async applyPromo({ state }, code) {
      console.log("applyPromo");
      console.log(code);
    },

    async getTokensInfo({ commit, dispatch }, tokens) {
      const tokensArr = [];
      for (let token of tokens) {
        try {
          const tokenAddr = token.address;
          if (tokenAddr) {
            const tokenInfo = await dispatch("getErc20TokenInfo", tokenAddr);
            const tokenDecimals = tokenInfo.decimals;
            const userBalance = new BigNumber(tokenInfo.userBalanceDatoshi)
              .div(new BigNumber(10).pow(tokenDecimals))
              .toFixed();
            tokensArr.push({
              ...token,
              ...tokenInfo,
              userBalance,
            });
          } else {
            tokensArr.push({
              ...token,
              userBalance: 0,
            });
          }
        } catch (error) {
          console.log(error);
        }
      }
      commit("SET_TOKENS", tokensArr);
    },

    cleanTransactionError({ commit }) {
      commit("SET_TRANSACTION_ERROR", "")
    },

    async getErc20TokenInfo({ state }, tokenAddr) {
      const userAddr = state.web3.address;
      const contract = ERC20(state.web3.instance, tokenAddr);
      const [name, symbol, decimals, userBalanceDatoshi] = await Promise.all([
        contract.methods.name().call(),
        contract.methods.symbol().call(),
        contract.methods.decimals().call(),
        contract.methods.balanceOf(userAddr).call(),
      ]);
      return {
        address: tokenAddr,
        name,
        symbol,
        decimals,
        userBalanceDatoshi,
      };
    },
  },
  mutations: {
    SET_INIT_LOADING(state, isLoading) {
      state.initLoading = isLoading;
    },
    SET_WEB3_PROVIDER(state, provider) {
      state.web3.provider = provider;
    },
    SET_WEB3_INSTANCE(state, web3) {
      state.web3.instance = web3;
    },
    SET_WEB3_CHAIN_ID(state, chainId) {
      state.web3.chainId = chainId;
    },
    SET_WEB3_IS_CONNECTED(state, isConnected) {
      state.web3.isConnected = isConnected;
    },
    SET_WEB3_USER_ADDRESS(state, addr) {
      state.web3.address = addr;
    },
    SET_TOKEN_PRICE(state, val) {
      state.tokenPrice = val;
    },
    SET_TOKENS(state, val) {
      state.tokens = val;
    },
    SET_TRANSACTION_HASH(state, val) {
      state.transactionHash = val;
    },

    SET_TRANSACTION_ERROR(state, val) {
      state.transactionError = val;
    },

    SET_MIN_AMOUNT(state, val) {
      state.minimumAmount = val;
    },

    SET_MAX_AMOUNT(state, val) {
      state.maximumAmount = val;
    },

    SET_VESTINGS_LIST(state, val) {
      state.vestingsList = val;
    }
  },
  // plugins: [createPersistedState()],
});

export default store;
